// Topic hubs for the learning center: pillar pages that group short content
// pieces into sub-categories (the Stripe resources model). Each article ref
// points at an existing page; keep titles in sync with the target page H1.

export interface LearnTopicArticle {
  path: string;
  title: string;
  description: string;
}

export interface LearnTopicSection {
  title: string;
  articles: readonly LearnTopicArticle[];
}

export interface LearnTopicStep {
  title: string;
  copy: string;
}

export interface LearnTopicTerm {
  term: string;
  definition: string;
}

export interface LearnTopicFaq {
  q: string;
  a: string;
}

export interface LearnTopic {
  slug: string;
  badge: string;
  title: string;
  metaTitle: string;
  description: string;
  promise: string;
  // Direct answer shown under the promise: what the topic covers, in plain words.
  answer: string;
  // Short practical guide (numbered steps), key terms, and FAQ (also FAQPage JSON-LD).
  guideTitle: string;
  guide: readonly LearnTopicStep[];
  terms: readonly LearnTopicTerm[];
  faq: readonly LearnTopicFaq[];
  sections: readonly LearnTopicSection[];
}

export const learnTopics: readonly LearnTopic[] = [
  {
    slug: 'invoicing-billing',
    badge: 'Topic hub',
    title: 'Invoicing & billing',
    metaTitle: 'Invoicing & Billing Guides | Workspace369 Learning Center',
    description:
      'Learn the fundamentals of invoicing: writing your first invoice, retainers, reminders, templates, and the invoice software that gets you paid on time.',
    promise:
      'Learn the fundamentals of invoicing — from writing your first invoice to retainers, reminders, and getting paid on time.',
    answer:
      'Invoicing is how you turn finished work into money: you send a clear request for payment, agree when it is due, and follow up until it lands. The short answer is to put the same few things on every invoice, bill on a schedule instead of from memory, and automate the reminders. The guides below cover each step, from your first invoice to retainers, deposits and late payments.',
    guideTitle: 'Invoicing basics in five steps',
    guide: [
      { title: 'Agree the terms before the work starts', copy: 'Put the price, the payment schedule and the due date in the proposal or contract. An invoice should never be the first time a client sees your terms.' },
      { title: 'Take a deposit where the work is large', copy: 'Many freelancers ask for a quarter to half of the fee up front. It confirms the client is committed and covers your first stretch of time.' },
      { title: 'Write a complete invoice', copy: 'Include your details, the client, a unique invoice number, issue and due dates, itemized lines with quantity and rate, the total, and a clear way to pay.' },
      { title: 'Send it on a schedule', copy: 'Invoice at the milestone, month end or retainer date you agreed, and send it the same day. Late invoices are paid late.' },
      { title: 'Follow up on a cadence', copy: 'Remind before the due date, on it, and after it, using short messages that name the invoice number. Escalate politely, and add a late fee only if your terms say so.' },
    ],
    terms: [
      { term: 'Invoice number', definition: 'The unique ID that ties a payment and a conversation to one invoice. Keep it sequential and never reuse one.' },
      { term: 'Payment terms', definition: 'The days allowed to pay, such as due on receipt, net 14 or net 30, counted from the invoice date.' },
      { term: 'Deposit invoice', definition: 'An invoice for an upfront part of the fee, credited against the final invoice.' },
      { term: 'Retainer', definition: 'A recurring payment that reserves your time or covers an agreed scope each month.' },
      { term: 'Kill fee', definition: 'What a client pays if they cancel a project partway through.' },
    ],
    faq: [
      {
        q: 'What should every invoice include?',
        a: 'Your business and client details, a unique invoice number, the issue and due dates, itemized line items with quantity, rate and amount, any taxes, the total due, the payment terms and a clear way to pay. The how to write an invoice guide walks through each part.',
      },
      {
        q: 'What payment terms should I use?',
        a: 'New clients and small jobs usually suit due on receipt or net 14. Established business clients often expect net 30. Whatever you choose, print it on the invoice and agree it in advance, because a term only works when it is stated and followed up.',
      },
      {
        q: 'How do I get paid faster?',
        a: 'Make payment expected and easy. Take deposits, state terms in the proposal, invoice on a schedule, itemize clearly, offer one-step payment options and send reminders automatically so follow-up happens even when you are busy delivering.',
      },
    ],
    sections: [
      {
        title: 'Getting started with invoicing',
        articles: [
          { path: '/learn/how-to-write-an-invoice', title: 'How to write an invoice', description: 'What to include so clients pay without questions.' },
          { path: '/learn/how-to-invoice-for-consulting-services', title: 'How to invoice for consulting services', description: 'The AP-ready format, terms, and follow-up cadence.' },
          { path: '/learn/what-is-an-invoice-number', title: 'What is an invoice number?', description: 'Formats, sequencing, and why duplicates stall payment.' },
          { path: '/learn/invoice-vs-receipt', title: 'Invoice vs receipt', description: 'The request before payment, the proof after.' },
          { path: '/learn/net-30-payment-terms', title: 'Net 30 payment terms', description: 'What net 30 means and when shorter terms win.' },
          { path: '/learn/deposit-invoice', title: 'Deposit invoice', description: 'Bill an upfront payment and credit it later.' },
          { path: '/tools/blank-invoice-template', title: 'Blank invoice template', description: 'A printable invoice to fill in by hand or as a PDF.' },
          { path: '/tools/invoice-template', title: 'Free invoice template', description: 'CSV templates that open in Excel and Google Sheets.' },
          { path: '/tools/invoice-generator', title: 'Free invoice generator', description: 'Build a print-ready invoice in the browser.' },
          { path: '/tools/estimate-template', title: 'Free estimate template', description: 'A clean estimate with valid-until date, as CSV.' },
        ],
      },
      {
        title: 'Getting paid on time',
        articles: [
          { path: '/learn/how-to-get-paid-faster', title: 'How to get paid faster', description: 'Terms, cadence, and the follow-up rhythm that works.' },
          { path: '/learn/retainer-fee', title: 'Retainer fee explained', description: 'The two retainer models and how to bill them automatically.' },
          { path: '/learn/kill-fee', title: 'Kill fee', description: 'What a client pays if they cancel mid-project.' },
          { path: '/tools/retainer-calculator', title: 'Retainer pricing calculator', description: 'Price a monthly retainer with overhead and margin built in.' },
          { path: '/learn/invoice-email-templates', title: 'Invoice email templates', description: 'What to write when you send, remind, and escalate.' },
          { path: '/tools/late-fee-calculator', title: 'Late fee calculator', description: 'Work out a fair late fee in seconds.' },
          { path: '/tools/payment-reminder-generator', title: 'Payment reminder generator', description: 'Reminder emails that get invoices paid.' },
          { path: '/tools/consulting-rate-calculator', title: 'Consulting rate calculator', description: 'The hourly, day, and project rate your income goal needs.' },
          { path: '/tools/payment-processing-fee-calculator', title: 'Payment fee calculator', description: 'What card and ACH fees really cost per invoice.' },
          { path: '/tools/upwork-vs-fiverr-fee-calculator', title: 'Upwork vs Fiverr fee calculator', description: 'What each platform leaves you on one job.' },
          { path: '/tools/invoice-due-date-calculator', title: 'Invoice due date calculator', description: 'Terms to due date, reminders, and first late day.' },
          { path: '/learn/what-is-a-subscription-fee', title: 'What is a subscription fee?', description: 'Recurring pricing explained, with examples.' },
          { path: '/blog/payment-reminder-message-examples', title: 'Payment reminder message examples', description: 'Copy-ready reminder wording for every stage.' },
        ],
      },
      {
        title: 'Invoice software',
        articles: [
          { path: '/blog/best-invoice-software', title: 'Best invoice software', description: 'The shortlist for service businesses.' },
          { path: '/blog/service-business-invoicing-software', title: 'Service business invoicing software', description: 'When a simple invoice app is not enough.' },
          { path: '/solutions/invoicing-and-project-management-software', title: 'Invoicing and project management software', description: 'The client-work loop, billed on schedule.' },
          { path: '/blog/estimate-invoice-payment-software', title: 'Estimate, quote, invoice & payment software', description: 'One flow from quote to paid.' },
        ],
      },
    ],
  },
  {
    slug: 'client-communication',
    badge: 'Topic hub',
    title: 'Client communication',
    metaTitle: 'Client Communication Guides | Workspace369 Learning Center',
    description:
      'Every channel your clients use: shared inbox guides, SMS and voicemail playbooks, and the client portal that keeps everyone in context.',
    promise:
      'Every channel your clients use — email, SMS, calls, and the shared inbox that keeps the whole team in context.',
    answer:
      'Good client communication means every client knows where to reach you, how fast to expect an answer, and what happens next, and your team can see the whole conversation. The short answer is to agree a channel and rhythm on day one, keep every message on the client record, and make follow-up a routine instead of a memory. The guides below cover the shared inbox, texts and calls, and client portals.',
    guideTitle: 'Client communication in five steps',
    guide: [
      { title: 'Set the channel and the rhythm on day one', copy: 'Tell each client where to reach you and how quickly they can expect a reply. Clear expectations prevent most of the anxious check-in messages.' },
      { title: 'Keep one thread per client', copy: 'Email, texts and call notes belong beside the client record, so anyone on the team can pick up a conversation without asking the client to repeat themselves.' },
      { title: 'Answer with context', copy: 'Open the history before you reply. A response that references the last decision or the open invoice shows the client you are paying attention.' },
      { title: 'Confirm decisions in writing', copy: 'After a call or meeting, send a short note with what was agreed, who does what and by when. Most scope arguments start with an unwritten decision.' },
      { title: 'Make follow-up routine', copy: 'Follow up after a proposal, after delivery and when an invoice goes quiet. Short messages that ask one question and make replying easy get answered.' },
    ],
    terms: [
      { term: 'Shared inbox', definition: 'One inbox the whole team works from, so no message depends on a single person being online.' },
      { term: 'Client portal', definition: 'A page where a client reviews approvals, invoices and updates without searching their email.' },
      { term: 'Response time', definition: 'The window you promise for a reply, such as one business day, stated up front and kept.' },
      { term: 'Out-of-office message', definition: 'An automatic reply that sets expectations while you are away and names who to contact.' },
    ],
    faq: [
      {
        q: 'What is the best way to communicate with clients?',
        a: 'Use the channel the client already prefers and agreed with you, then keep a record of it. A shared inbox for email and texts, and a portal for approvals and invoices, keeps conversations in one place instead of scattered across personal inboxes and phones.',
      },
      {
        q: 'How quickly should I reply to a client?',
        a: 'Pick a window you can always meet, such as the same or next business day, and tell clients what it is. A fast acknowledgement that names when the full answer will arrive is better than silence while you prepare a perfect reply.',
      },
      {
        q: 'How do I follow up without nagging?',
        a: 'Keep each follow-up to a few sentences, reference the specific proposal, delivery or invoice, and ask one question. Schedule the cadence in advance so it is neutral and routine, not a reaction to feeling ignored.',
      },
    ],
    sections: [
      {
        title: 'Shared inbox',
        articles: [
          { path: '/blog/google-workspace-shared-inbox', title: 'Google Workspace shared inbox', description: 'The setup that works, and when you outgrow it.' },
          { path: '/learn/out-of-office-message-examples', title: 'Out of office message examples', description: 'Copy-ready templates that keep clients informed.' },
          { path: '/learn/client-follow-up-email', title: 'Client follow-up email examples', description: 'Short templates for every stage of the work.' },
          { path: '/solutions/shared-inbox-software', title: 'Shared inbox software', description: 'One inbox for the whole client relationship.' },
          { path: '/solutions/sms-inbox-for-client-work', title: 'SMS inbox for client work', description: 'Texts beside the client record.' },
          { path: '/blog/sms-inbox-for-business', title: 'SMS inbox for business', description: 'Texting with clients without losing the thread.' },
        ],
      },
      {
        title: 'SMS, calls & voicemail',
        articles: [
          { path: '/blog/appointment-reminder-text-examples', title: 'Appointment reminder text examples', description: 'Messages that cut no-shows.' },
          { path: '/blog/business-voicemail-greeting-examples', title: 'Business voicemail greeting examples', description: 'Greetings that sound like a real company.' },
          { path: '/blog/voicemail-transcription-software', title: 'Voicemail transcription software', description: 'Read voicemails instead of playing them.' },
          { path: '/solutions/ai-call-summary-software', title: 'AI call summary software', description: 'Every call, summarized to the record.' },
        ],
      },
      {
        title: 'Client portals',
        articles: [
          { path: '/learn/client-communication-best-practices', title: 'Client communication best practices', description: 'The habits clients actually notice.' },
          { path: '/solutions/client-portal-for-agencies', title: 'Client portal software for agencies', description: 'Approvals, billing, and follow-up in one portal.' },
          { path: '/blog/client-portal-software-for-agencies', title: 'Client portal software guide', description: 'What to look for before you buy.' },
        ],
      },
    ],
  },
  {
    slug: 'client-management',
    badge: 'Topic hub',
    title: 'Client management',
    metaTitle: 'Client Management Guides | Workspace369 Learning Center',
    description:
      'Client records, follow-up, onboarding, and the five-step client management process that keeps service businesses organized.',
    promise:
      'Records, follow-up, and the five-step process that keeps clients — without spreadsheets.',
    answer:
      'Client management is the routine of knowing who each client is, what you promised them, what is open right now and what happens next. The short answer is one record per client, a repeatable five-step process from intake to follow-up, and no spreadsheets holding the parts together. The guides below cover CRM basics, the process itself and client onboarding.',
    guideTitle: 'The client management process in five steps',
    guide: [
      { title: 'Intake', copy: 'Capture every inquiry as a tracked request with the same questions each time, so nothing sits in an inbox waiting for someone to remember it.' },
      { title: 'Onboarding', copy: 'Collect contact and billing details, the goal in the client\'s words, access and constraints once, and set expectations about channel, timing and approvals.' },
      { title: 'Delivery', copy: 'Show progress the client can see and keep tasks, files and conversations attached to the client record.' },
      { title: 'Billing', copy: 'Invoice on the schedule you agreed, whether that is a milestone, month end or retainer date, rather than from memory.' },
      { title: 'Follow-up', copy: 'Check in after delivery, before renewals and when things go quiet. A steady follow-up cadence is what turns one project into a long relationship.' },
    ],
    terms: [
      { term: 'CRM', definition: 'Customer relationship management: a system that keeps every client, conversation and commitment in one place.' },
      { term: 'Client record', definition: 'The single page holding contacts, history, files, work and billing for one client.' },
      { term: 'Intake form', definition: 'A structured set of questions a new client answers once, before the work starts.' },
      { term: 'Onboarding', definition: 'The steps between a yes and the first day of work: details collected, expectations set, access arranged.' },
    ],
    faq: [
      {
        q: 'What is a CRM and do I need one?',
        a: 'A CRM is a system that keeps contact details, notes, history and open work for every client in one place. You need one when client information lives in more than one head, inbox or spreadsheet, because that is when things start falling through.',
      },
      {
        q: 'What is the difference between a CRM and project management?',
        a: 'A CRM tracks the relationship: who the client is, what was said and what is next. Project management tracks the work: tasks, deadlines and deliverables. Service businesses usually need both connected, so the work sits on the client record.',
      },
      {
        q: 'What should I collect when I onboard a new client?',
        a: 'Collect contact and billing details, who approves work and who pays invoices, the goal and scope in the client\'s words, practical logistics like access and brand assets, and constraints such as deadlines. Ask once, in one intake form, and attach the answers to the client record.',
      },
    ],
    sections: [
      {
        title: 'Fundamentals',
        articles: [
          { path: '/learn/what-is-a-crm', title: 'What is a CRM?', description: 'The plain-English answer for service businesses.' },
          { path: '/learn/crm-vs-project-management', title: 'CRM vs project management', description: 'Which one you need, and when it is both.' },
          { path: '/blog/client-management-process', title: 'The client management process', description: 'Five steps that keep clients.' },
          { path: '/blog/how-to-manage-clients-as-a-freelancer', title: 'How to manage clients as a freelancer', description: 'Records, follow-up, billing — one loop.' },
        ],
      },
      {
        title: 'CRM guides',
        articles: [
          { path: '/solutions/client-management-software', title: 'Client management software', description: 'One record per client, everything attached.' },
          { path: '/solutions/workspace-crm', title: 'Workspace CRM', description: 'The client record and the work in one place.' },
          { path: '/blog/best-crm-for-consultants', title: 'Best CRM for consultants', description: 'The consultant-fit shortlist.' },
          { path: '/blog/best-crm-for-solo-entrepreneurs', title: 'Best CRM for solo entrepreneurs', description: 'Solo-simple picks.' },
        ],
      },
      {
        title: 'Onboarding',
        articles: [
          { path: '/learn/client-intake-checklist', title: 'Client intake checklist', description: 'What to collect before work starts.' },
          { path: '/learn/client-onboarding-questionnaire', title: 'Client onboarding questionnaire', description: '30 questions to ask new clients.' },
          { path: '/learn/client-intake-form-template', title: 'Client intake form template', description: 'The fields to ask before a proposal.' },
          { path: '/learn/creative-brief-template', title: 'Creative brief template', description: 'One page everyone agrees on before work starts.' },
          { path: '/learn/how-to-fire-a-client', title: 'How to fire a client', description: 'End it professionally, with an email template.' },
          { path: '/blog/best-client-onboarding-software', title: 'Best client onboarding software', description: 'Onboarding compared.' },
          { path: '/blog/client-onboarding-checklist-for-agencies', title: 'Client onboarding checklist for agencies', description: 'The agency version.' },
        ],
      },
    ],
  },
  {
    slug: 'proposals-pricing',
    badge: 'Topic hub',
    title: 'Proposals & pricing',
    metaTitle: 'Proposals & Pricing Guides | Workspace369 Learning Center',
    description:
      'Scopes, proposals, and pricing that protects the margin: scope of work, quotes, pricing models, and the money habits behind a healthy business.',
    promise:
      'Scopes, proposals, and pricing that protects the margin — from quote to signed approval.',
    answer:
      'A proposal is the step between a conversation and an invoice: it restates the client\'s problem, defines the scope, shows the price and gives them one easy way to say yes. The short answer is to price from your real delivery cost, offer options where you can, state the terms clearly and follow up on a schedule. The guides below cover proposals and scopes, pricing models and the money habits behind a healthy business.',
    guideTitle: 'From quote to signed approval in five steps',
    guide: [
      { title: 'Restate the problem and the outcome', copy: 'Open with the client\'s situation in their own words and what changes for them when the work is done.' },
      { title: 'Define the scope', copy: 'Say what is included, what is excluded and what assumptions you are making. A clear scope is the best protection against unpaid extra work.' },
      { title: 'Price it from your cost', copy: 'Work from the hours at a loaded rate, plus expenses, plus margin. Choose hourly for uncertain scope, a fixed fee for defined deliverables and a retainer for ongoing work.' },
      { title: 'Offer options and ask for a deposit', copy: 'Two or three packages turn a yes or no decision into a which-one decision. A deposit turns the approval into a commitment.' },
      { title: 'Send fast and follow up', copy: 'Send within a few days of the conversation, check in at about two days, ask a direct next-step question at about five, and close politely after that.' },
    ],
    terms: [
      { term: 'Scope of work', definition: 'The written list of deliverables, boundaries and assumptions that the price covers.' },
      { term: 'Proposal', definition: 'A document that sells the approach and outcome alongside the price. A quote states the price of an already defined scope.' },
      { term: 'Fixed fee', definition: 'One agreed price for defined deliverables, regardless of the hours spent.' },
      { term: 'Margin', definition: 'What is left of the price after delivery costs, which funds overhead, taxes and profit.' },
      { term: 'Customer lifetime value', definition: 'The total revenue a client brings over the whole relationship, which helps you judge how much to invest in winning and keeping them.' },
    ],
    faq: [
      {
        q: 'What should a business proposal include?',
        a: 'The client\'s problem and desired outcome, the scope with inclusions and exclusions, the price with options where possible, the terms and timeline, and a direct way to approve. One to two pages is enough for most service work.',
      },
      {
        q: 'How do I choose between hourly, fixed fee and a retainer?',
        a: 'Use hourly when the scope is uncertain, a fixed fee when the deliverables are defined, and a retainer for an ongoing relationship. Whichever you pick, review it against actual time spent, because a model fails quietly when the scope grows and the price does not move.',
      },
      {
        q: 'Should I ask for a deposit?',
        a: 'Usually yes. A deposit of a quarter to half of the fee before work starts shows the client is committed and covers your early time. Put the amount and due date in the proposal so there is nothing to negotiate later.',
      },
    ],
    sections: [
      {
        title: 'Proposals & scopes',
        articles: [
          { path: '/learn/how-to-write-a-proposal', title: 'How to write a proposal', description: 'The six sections that get a fast yes.' },
          { path: '/learn/consulting-proposal-template', title: 'Consulting proposal template', description: 'Nine sections and pricing options.' },
          { path: '/templates/web-design-proposal', title: 'Web design proposal template', description: 'Free PDF and Word download: scope, packages, timeline and a filled-in example.' },
          { path: '/learn/graphic-design-proposal-template', title: 'Graphic design proposal template', description: 'Concepts, revisions, rights and packages.' },
          { path: '/blog/scope-of-work-software', title: 'Scope of work software', description: 'Scopes clients approve online.' },
          { path: '/features/quoting', title: 'Proposals & quotes', description: 'Quotes built from the client record.' },
          { path: '/learn/liability-waiver-template', title: 'Liability waiver template', description: 'The sections every waiver needs.' },
          { path: '/learn/retainer-agreement-template', title: 'Retainer agreement template', description: 'Scope, rollover and overage clauses.' },
          { path: '/learn/graphic-design-contract-template', title: 'Graphic design contract template', description: 'Revisions, source files and rights.' },
          { path: '/learn/web-design-contract-template', title: 'Web design contract template', description: 'Content, hosting, launch and support.' },
        ],
      },
      {
        title: 'Pricing your work',
        articles: [
          { path: '/learn/how-to-price-agency-work', title: 'How to price agency work', description: 'Models that protect margin.' },
          { path: '/tools/hourly-rate-calculator', title: 'Hourly rate calculator', description: 'The rate you actually need.' },
          { path: '/learn/agency-profit-basics', title: 'Agency profit basics', description: 'The numbers behind a healthy agency.' },
          { path: '/blog/agency-profitability-software', title: 'Agency profitability software', description: 'Margin visibility without the enterprise suite.' },
        ],
      },
      {
        title: 'Money habits',
        articles: [
          { path: '/learn/customer-lifetime-value', title: 'Customer lifetime value', description: 'The number behind every pricing and retention call.' },
          { path: '/learn/freelancer-tax-calendar', title: 'Freelancer tax calendar', description: 'The dates that decide your year.' },
          { path: '/learn/expense-tracking-for-taxes', title: 'Expense tracking for taxes', description: 'The weekly habit, not the April project.' },
        ],
      },
    ],
  },
];

export const learnTopicsBySlug: Record<string, LearnTopic> = {};
for (const topic of learnTopics) {
  learnTopicsBySlug[topic.slug] = topic;
}
